<script>
import { mapGetters } from 'vuex';
import router from '../../../../index';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';

import Spinner from 'shared/components/Spinner.vue';
import PageHeader from '../../SettingsSubPageHeader.vue';
import AgentSelector from '../AgentSelector.vue';

export default {
  components: {
    Spinner,
    PageHeader,
    AgentSelector,
  },
  validations: {
    selectedAgents: {
      isEmpty() {
        return !!this.selectedAgents.length;
      },
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      selectedAgents: [],
      memberSettings: {}, // GK-HUB: { [agentId]: { receives_assignment } }
      isCreating: false,
    };
  },

  computed: {
    ...mapGetters({
      agentList: 'agents/getAgents',
      uiFlags: 'teamMembers/getUIFlags',
    }),

    teamId() {
      return this.$route.params.teamId;
    },
    headerTitle() {
      return this.$t('TEAMS_SETTINGS.EDIT_FLOW.AGENTS.TITLE', {
        teamName: this.currentTeam.name,
      });
    },
    currentTeam() {
      return this.$store.getters['teams/getTeam'](this.teamId);
    },
    teamMembers() {
      return this.$store.getters['teamMembers/getTeamMembers'](this.teamId);
    },
    showAgentsList() {
      const { id } = this.currentTeam;
      return id && !this.uiFlags.isFetching;
    },
  },

  async mounted() {
    const { teamId } = this.$route.params;
    this.$store.dispatch('agents/get');
    try {
      await this.$store.dispatch('teamMembers/get', {
        teamId,
      });
      // GK-HUB: pré-carrega o papel de cada membro existente (default recebe)
      this.memberSettings = Object.fromEntries(
        this.teamMembers.map(item => [
          item.id,
          { receives_assignment: item.receives_assignment !== false },
        ])
      );
      this.updateSelectedAgents(this.teamMembers.map(item => item.id));
    } catch {
      this.updateSelectedAgents([]);
    }
  },

  methods: {
    updateSelectedAgents(newAgentList) {
      this.v$.selectedAgents.$touch();
      this.selectedAgents = [...newAgentList];
      // GK-HUB (#115): default por role — agente recém-adicionado nasce recebedor; não-agente
      // (admin/supervisor/manager) nasce OBSERVADOR. Membros já existentes mantêm a flag pré-carregada
      // (mounted). Alinha a UI ao gate desacoplado do backend; sem isso não-agente novo viraria recebedor.
      const next = { ...this.memberSettings };
      newAgentList.forEach(id => {
        if (next[id] === undefined) {
          const agent = this.agentList.find(item => item.id === id);
          next[id] = { receives_assignment: agent?.role === 'agent' };
        }
      });
      this.memberSettings = next;
    },
    // GK-HUB: atualiza o papel (recebe-fila/observador) de um agente
    updateMemberSetting(agentId, receives) {
      this.memberSettings = {
        ...this.memberSettings,
        [agentId]: { receives_assignment: receives },
      };
    },
    async addAgents() {
      this.isCreating = true;
      const { teamId, selectedAgents } = this;

      try {
        // GK-HUB: monta members[{ user_id, receives_assignment }]
        const members = selectedAgents.map(id => ({
          user_id: id,
          receives_assignment:
            this.memberSettings[id]?.receives_assignment !== false,
        }));
        await this.$store.dispatch('teamMembers/update', {
          teamId,
          members,
        });
        router.replace({
          name: 'settings_teams_edit_finish',
          params: {
            page: 'edit',
            teamId,
          },
        });
        this.$store.dispatch('teams/get');
      } catch (error) {
        useAlert(error.message);
      }
      this.isCreating = false;
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-8 col-span-6">
    <form
      class="flex flex-wrap mx-0 overflow-x-auto"
      @submit.prevent="addAgents"
    >
      <div class="w-full">
        <PageHeader
          :header-title="headerTitle"
          :header-content="$t('TEAMS_SETTINGS.EDIT_FLOW.AGENTS.DESC')"
        />
      </div>

      <div class="w-full">
        <div v-if="v$.selectedAgents.$error">
          <p class="error-message pb-2">
            {{ $t('TEAMS_SETTINGS.ADD.AGENT_VALIDATION_ERROR') }}
          </p>
        </div>
        <AgentSelector
          v-if="showAgentsList"
          :agent-list="agentList"
          :selected-agents="selectedAgents"
          :update-selected-agents="updateSelectedAgents"
          :member-settings="memberSettings"
          :update-member-setting="updateMemberSetting"
          :is-working="isCreating"
          :submit-button-text="
            $t('TEAMS_SETTINGS.EDIT_FLOW.AGENTS.BUTTON_TEXT')
          "
        />
        <Spinner v-else />
      </div>
    </form>
  </div>
</template>
